// public/js/features/assets/asset-shipments.js
// "Shipments" on the asset detail page: the manual A-to-B object beside the
// trips (docs/trip-detection.md, Layer 3).
//
// A shipment says what was SUPPOSED to happen. The trips underneath say
// what did. Keeping them apart is the point, so this section never edits an
// actual time - only the plan, and whether the plan is still live.
import { escapeHtml } from '../../utils/escape.js';
import { loadPlaces } from '../pois/poi-locate.js';

const L = () => navigator.language;
const fmt = (iso) => iso
  ? new Date(iso).toLocaleString(L(), { dateStyle: 'medium', timeStyle: 'short' })
  : '—';

const BADGE = {
  planned:    ['badge-soft-gray',  'Planned'],
  in_transit: ['badge-soft-blue',  'In transit'],
  arrived:    ['badge-soft-green', 'Arrived'],
  overdue:    ['badge-soft-red',   'Overdue'],
  abandoned:  ['badge-soft-amber', 'Given up'],
  cancelled:  ['badge-soft-gray',  'Cancelled'],
};

function row(s) {
  const [cls, label] = BADGE[s.displayStatus] || BADGE.planned;
  const open = s.status === 'planned' || s.status === 'in_transit';
  return `
    <div class="list-group-item py-2" data-shipment="${escapeHtml(s.shipmentId)}">
      <div class="d-flex flex-wrap align-items-center gap-2">
        <span class="badge ${cls}">${label}</span>
        <span class="fw-semibold small">
          ${escapeHtml(s.originName || s.originPoiId)}
          <i class="fa-solid fa-arrow-right-long text-muted mx-1"></i>
          ${escapeHtml(s.destinationName || s.destinationPoiId)}
        </span>
        ${s.reference ? `<span class="text-muted small">${escapeHtml(s.reference)}</span>` : ''}
        ${open ? `<button type="button" class="btn btn-sm btn-link text-danger p-0 ms-auto js-cancel-shipment">Cancel</button>` : ''}
      </div>
      <div class="small text-muted mt-1 d-flex flex-wrap gap-3">
        <span>Expected ${fmt(s.expectedDeparture)} → ${fmt(s.expectedArrival)}</span>
        ${s.actualDeparture ? `<span>Left ${fmt(s.actualDeparture)}</span>` : ''}
        ${s.actualArrival ? `<span>Arrived ${fmt(s.actualArrival)}</span>` : ''}
      </div>
    </div>`;
}

function modalHtml(places) {
  const opts = places.map(p =>
    `<option value="${escapeHtml(p.poiId || p.id)}">${escapeHtml(p.name || p.poiId || p.id)}</option>`).join('');
  return `
    <div class="modal fade" id="new-shipment-modal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title"><i class="fa-solid fa-route me-2 text-secondary"></i>New shipment</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <p class="small text-muted">What this asset is supposed to do. Its arrival is
              recognised on its own, from the positions it reports.</p>
            <div class="row g-2 mb-2">
              <div class="col-6">
                <label class="form-label small mb-1">From</label>
                <select class="form-select form-select-sm js-sh-origin"><option value="">Choose…</option>${opts}</select>
              </div>
              <div class="col-6">
                <label class="form-label small mb-1">To</label>
                <select class="form-select form-select-sm js-sh-dest"><option value="">Choose…</option>${opts}</select>
              </div>
            </div>
            <div class="row g-2 mb-2">
              <div class="col-6">
                <label class="form-label small mb-1">Expected departure</label>
                <input type="datetime-local" class="form-control form-control-sm js-sh-dep">
              </div>
              <div class="col-6">
                <label class="form-label small mb-1">Expected arrival</label>
                <input type="datetime-local" class="form-control form-control-sm js-sh-arr">
              </div>
            </div>
            <label class="form-label small mb-1">Reference <span class="text-muted">(order number, customer…)</span></label>
            <input type="text" class="form-control form-control-sm js-sh-ref" maxlength="120">
            <div class="text-danger small mt-2 js-sh-error" hidden></div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">Close</button>
            <button type="button" class="btn btn-primary js-sh-save">Create</button>
          </div>
        </div>
      </div>
    </div>`;
}

const toIso = (local) => local ? new Date(local).toISOString() : null;

export async function renderAssetShipmentsSection(container, assetUUID) {
  if (!container) return;
  const base = `api/v1/assets/${encodeURIComponent(assetUUID)}/shipments`;

  const paint = (shipments) => {
    container.innerHTML = `
      <div class="d-flex align-items-center gap-2 mb-2">
        <button type="button" class="btn btn-sm btn-outline-primary js-new-shipment">
          <i class="fa-solid fa-plus me-1"></i>New shipment
        </button>
        ${shipments.length ? '' : '<span class="small text-muted">None yet.</span>'}
      </div>
      ${shipments.length ? `<div class="list-group">${shipments.map(row).join('')}</div>` : ''}`;

    container.querySelector('.js-new-shipment')?.addEventListener('click', openModal);
    container.querySelectorAll('.js-cancel-shipment').forEach(btn => {
      btn.addEventListener('click', async (ev) => {
        const id = ev.currentTarget.closest('[data-shipment]')?.dataset.shipment;
        if (!id) return;
        ev.currentTarget.disabled = true;
        try {
          const res = await window.authenticatedFetch(`${base}/${encodeURIComponent(id)}`, {
            method: 'PATCH', headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ status: 'cancelled' }),
          });
          if (!res.ok) throw new Error('cancel failed');
          await load();
        } catch {
          window.showToast?.('Could not cancel the shipment', 'danger');
          ev.currentTarget.disabled = false;
        }
      });
    });
  };

  async function openModal() {
    let places = [];
    try { places = await loadPlaces(); } catch { places = []; }
    if (!places.length) { window.showToast?.('No POIs to pick from yet', 'info'); return; }

    document.getElementById('new-shipment-modal')?.remove();
    document.body.insertAdjacentHTML('beforeend', modalHtml(places));
    const root = document.getElementById('new-shipment-modal');
    const modal = new window.bootstrap.Modal(root);
    modal.show();
    root.addEventListener('hidden.bs.modal', () => root.remove());

    root.querySelector('.js-sh-save').addEventListener('click', async (ev) => {
      const err = root.querySelector('.js-sh-error');
      const body = {
        originPoiId: root.querySelector('.js-sh-origin').value,
        destinationPoiId: root.querySelector('.js-sh-dest').value,
        expectedDeparture: toIso(root.querySelector('.js-sh-dep').value),
        expectedArrival: toIso(root.querySelector('.js-sh-arr').value),
        reference: root.querySelector('.js-sh-ref').value.trim() || null,
      };
      const show = (m) => { err.textContent = m; err.hidden = false; };
      if (!body.originPoiId || !body.destinationPoiId) return show('Pick where it starts and where it is going.');
      if (body.originPoiId === body.destinationPoiId) return show('Origin and destination have to differ.');

      ev.currentTarget.disabled = true;
      try {
        const res = await window.authenticatedFetch(base, {
          method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error((await res.json()).error || 'create failed');
        modal.hide();
        await load();
      } catch (e) {
        show(e.message || 'Could not create the shipment');
        ev.currentTarget.disabled = false;
      }
    });
  }

  async function load() {
    try {
      const res = await window.authenticatedFetch(base);
      paint(res.ok ? ((await res.json()).shipments || []) : []);
    } catch {
      container.innerHTML = '<div class="text-danger small">Failed to load shipments.</div>';
    }
  }

  container.innerHTML = '<div class="text-muted small">Loading shipments…</div>';
  await load();
}
